/**
 * @quotech/ui - Navigation Tracking
 * 
 * Tracks navigation usage for AI-based auto-ordering.
 * - Counts clicks on nav items
 * - Tracks time spent on pages
 * - Decays old data over time
 * - Provides smart ordering recommendations
 */

import type { HubId, NavItem, ExternalLink, NavUsageStats } from '../components/types'

const STORAGE_KEY = 'quotech_nav_usage'
const DECAY_DAYS = 30 // Data older than this gets decayed
const DECAY_FACTOR = 0.9 // Multiply old counts by this factor

// =============================================================================
// STORAGE
// =============================================================================

interface StoredUsageData {
  stats: NavUsageStats[]
  lastDecay: number
  version: number
}

function getStoredData(): StoredUsageData {
  try {
    const stored = localStorage.getItem(STORAGE_KEY)
    if (!stored) {
      return { stats: [], lastDecay: Date.now(), version: 1 }
    }
    return JSON.parse(stored)
  } catch {
    return { stats: [], lastDecay: Date.now(), version: 1 }
  }
}

function saveData(data: StoredUsageData): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(data))
  } catch {
    // Storage unavailable
  }
}

// =============================================================================
// DECAY
// =============================================================================

/**
 * Apply decay to old usage data
 * Called periodically to prevent stale data from dominating
 */
function applyDecay(data: StoredUsageData): StoredUsageData {
  const now = Date.now()
  const daysSinceDecay = (now - data.lastDecay) / (1000 * 60 * 60 * 24)
  
  // Only decay if more than a week has passed
  if (daysSinceDecay < 7) {
    return data
  }
  
  const decayedStats = data.stats.map(stat => {
    const daysSinceClick = (now - stat.lastClickedAt) / (1000 * 60 * 60 * 24)
    if (daysSinceClick > DECAY_DAYS) {
      return {
        ...stat,
        clickCount: Math.floor(stat.clickCount * DECAY_FACTOR),
      }
    }
    return stat
  }).filter(stat => stat.clickCount > 0) // Remove items with 0 clicks
  
  return {
    stats: decayedStats,
    lastDecay: now,
    version: data.version,
  }
}

// =============================================================================
// TRACKING
// =============================================================================

/**
 * Track a navigation click
 */
export function trackNavClick(itemId: string, hubId: HubId): void {
  const data = getStoredData()
  const decayedData = applyDecay(data)
  
  const existingIndex = decayedData.stats.findIndex(
    s => s.itemId === itemId && s.hubId === hubId
  )
  
  if (existingIndex >= 0) {
    // Update existing
    decayedData.stats[existingIndex] = {
      ...decayedData.stats[existingIndex],
      clickCount: decayedData.stats[existingIndex].clickCount + 1,
      lastClickedAt: Date.now(),
    }
  } else {
    // Create new
    decayedData.stats.push({
      itemId,
      hubId,
      clickCount: 1,
      lastClickedAt: Date.now(),
    })
  }
  
  saveData(decayedData)
}

/**
 * Track time spent on a page
 */
let pageEnterTime: number | null = null
let currentPageId: string | null = null
let currentHubId: HubId | null = null

export function trackPageEnter(itemId: string, hubId: HubId): void {
  // Save time for previous page if exists
  if (pageEnterTime && currentPageId && currentHubId) {
    const timeSpent = Date.now() - pageEnterTime
    updateTimeSpent(currentPageId, currentHubId, timeSpent)
  }
  
  pageEnterTime = Date.now()
  currentPageId = itemId
  currentHubId = hubId
}

function updateTimeSpent(itemId: string, hubId: HubId, timeMs: number): void {
  // Only count if reasonable time (1 second to 30 minutes)
  if (timeMs < 1000 || timeMs > 30 * 60 * 1000) return
  
  const data = getStoredData()
  const existingIndex = data.stats.findIndex(
    s => s.itemId === itemId && s.hubId === hubId
  )
  
  if (existingIndex >= 0) {
    const stat = data.stats[existingIndex]
    const prevAvg = stat.avgTimeSpent || 0
    const prevCount = stat.clickCount || 1
    // Calculate rolling average
    const newAvg = (prevAvg * (prevCount - 1) + timeMs) / prevCount
    data.stats[existingIndex].avgTimeSpent = newAvg
    saveData(data)
  }
}

// =============================================================================
// SCORING & ORDERING
// =============================================================================

interface NavScore {
  itemId: string
  score: number
  clickCount: number
  lastClickedAt: number
}

/**
 * Calculate score for a nav item
 * Score considers:
 * - Click count (primary)
 * - Recency (secondary)
 * - Time spent (tertiary)
 */
function calculateScore(stat: NavUsageStats): number {
  const now = Date.now()
  const hoursSinceClick = (now - stat.lastClickedAt) / (1000 * 60 * 60)
  
  // Click count is primary (weight: 1.0)
  const clickScore = stat.clickCount
  
  // Recency bonus (max 10 points, decays over 72 hours)
  const recencyScore = Math.max(0, 10 - (hoursSinceClick / 7.2))
  
  // Time spent bonus (max 5 points if avg > 2 minutes)
  const timeScore = stat.avgTimeSpent 
    ? Math.min(5, (stat.avgTimeSpent / 120000) * 5) 
    : 0
  
  return clickScore + recencyScore + timeScore
}

/**
 * Order the unanchored tail by RECENCY: the most-recently-clicked item
 * floats to the front, then the rest of the clicked items in descending
 * lastClickedAt order, then the never-clicked items in their original
 * (config) order. Deterministic — a single click on an item is enough to
 * send it to the top, which the click-count-weighted `usage` strategy does
 * NOT guarantee (a historically-frequent item would outscore one click).
 * Used by the master rail's "click → absolute top" smart structuring.
 */
function orderByRecency<T extends NavItem>(
  items: T[],
  scores: Map<string, NavScore>,
): T[] {
  const clicked: T[] = []
  const unclicked: T[] = []
  for (const item of items) {
    if (scores.has(item.id)) clicked.push(item)
    else unclicked.push(item)
  }
  clicked.sort(
    (a, b) =>
      (scores.get(b.id)?.lastClickedAt || 0) - (scores.get(a.id)?.lastClickedAt || 0),
  )
  return [...clicked, ...unclicked]
}

/**
 * Get ordered nav items based on usage.
 *
 * `strategy`:
 *   - 'usage'   (default) — click-count weighted score (recency/time as
 *     secondary tie-breakers). The historical SAM-style behaviour: heavily
 *     used items float up, a single click rarely reorders the rail.
 *   - 'recency' — most-recently-clicked floats to the front (see
 *     `orderByRecency`). Pair with `preserveFirst: 0` so the clicked item
 *     reaches index 0 ("click → absolute top", operator 2026-06-19).
 *
 * `strategy` is opt-in: omitting it preserves the exact prior behaviour, so
 * existing callers (SAM's own rail, the email app) are untouched.
 */
export function getOrderedNavItems<T extends NavItem>(
  items: T[],
  hubId: HubId,
  options: {
    preserveFirst?: number // Keep first N items in original position
    enabled?: boolean
    strategy?: 'usage' | 'recency'
  } = {}
): T[] {
  const { preserveFirst = 1, enabled = true, strategy = 'usage' } = options
  
  if (!enabled || items.length <= preserveFirst) {
    return items
  }
  
  const data = getStoredData()
  const hubStats = data.stats.filter(s => s.hubId === hubId)
  
  // Calculate scores
  const scores = new Map<string, NavScore>()
  hubStats.forEach(stat => {
    scores.set(stat.itemId, {
      itemId: stat.itemId,
      score: calculateScore(stat),
      clickCount: stat.clickCount,
      lastClickedAt: stat.lastClickedAt,
    })
  })
  
  // Split items: preserve first N, sort rest
  const preserved = items.slice(0, preserveFirst)
  const toSort = items.slice(preserveFirst)
  
  const sorted =
    strategy === 'recency'
      ? orderByRecency(toSort, scores)
      : // Sort by score (descending). slice() so we never mutate the input.
        toSort.slice().sort((a, b) => {
          const scoreA = scores.get(a.id)?.score || 0
          const scoreB = scores.get(b.id)?.score || 0
          return scoreB - scoreA
        })
  
  return [...preserved, ...sorted]
}

/**
 * Get ordered hubs based on usage
 */
export function getOrderedHubs<T extends ExternalLink>(
  hubs: T[],
  currentHubId?: HubId
): T[] {
  const data = getStoredData()
  
  // Aggregate scores by hub
  const hubScores = new Map<string, number>()
  data.stats.forEach(stat => {
    const current = hubScores.get(stat.hubId) || 0
    hubScores.set(stat.hubId, current + calculateScore(stat))
  })
  
  // Sort hubs by score, but keep current hub out of sorting
  return [...hubs].sort((a, b) => {
    // Current hub always stays in its position
    if (a.id === currentHubId || b.id === currentHubId) return 0
    
    const scoreA = a.id ? hubScores.get(a.id as HubId) || 0 : 0
    const scoreB = b.id ? hubScores.get(b.id as HubId) || 0 : 0
    return scoreB - scoreA
  })
}

// =============================================================================
// UTILITIES
// =============================================================================

/**
 * Get usage stats for a specific item
 */
export function getItemStats(itemId: string, hubId: HubId): NavUsageStats | null {
  const data = getStoredData()
  return data.stats.find(s => s.itemId === itemId && s.hubId === hubId) || null
}

/**
 * Get all usage stats
 */
export function getAllStats(): NavUsageStats[] {
  return getStoredData().stats
}

/**
 * Clear all usage data
 */
export function clearUsageData(): void {
  localStorage.removeItem(STORAGE_KEY)
}

/**
 * Export usage data (for debugging/analytics)
 */
export function exportUsageData(): StoredUsageData {
  return getStoredData()
}

// =============================================================================
// HOOKS
// =============================================================================

/**
 * React hook to track navigation and get ordered items
 * Usage:
 *   const { orderedItems, trackClick } = useNavTracking(navItems, 'crm')
 */
export function createNavTrackingHook<T extends NavItem>(
  items: T[],
  hubId: HubId,
  options?: { preserveFirst?: number; enabled?: boolean }
) {
  return {
    orderedItems: getOrderedNavItems(items, hubId, options),
    trackClick: (itemId: string) => trackNavClick(itemId, hubId),
    trackPageEnter: (itemId: string) => trackPageEnter(itemId, hubId),
    getStats: (itemId: string) => getItemStats(itemId, hubId),
  }
}

